import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useFormContext } from "react-hook-form";

import ProjectAPI from "@/src/api/ProjectAPI";
import { MultiComboboxBase } from "@/src/components/form-fields/MultiCombobox";
import { SegmentedFieldBase } from "@/src/components/form-fields/SegmentedField";
import { getCountryOptions } from "@/src/utils/data/Countries";
import { isGeoblockActive } from "@/src/utils/geoblock";

import type { JSX } from "react";

import type {
  GeoblockMode,
  GeoCountry,
  GeoCountryGroup,
} from "@/src/api/geoblock.types";
import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

export type { GeoblockMode };

export const GEOBLOCK_OPTION_LIST: { value: GeoblockMode; labelKey: string }[] =
  [
    { value: "disabled", labelKey: "none" },
    { value: "allow", labelKey: "allow" },
    { value: "deny", labelKey: "block" },
  ];

export interface GeoSelection {
  countries: GeoCountry[];
  countryGroups: GeoCountryGroup[];
}

interface GeoFieldBaseProps {
  geoblockMode: GeoblockMode;
  selectedCountries: GeoCountry[];
  selectedCountryGroups: GeoCountryGroup[];
  disabled?: boolean;
  error?: string;
  popoverContainer?: HTMLElement | null;
  popoverClassName?: string;
  onModeChange: (mode: GeoblockMode) => void;
  onSelectionChange: (selection: GeoSelection) => void;
}

/**
 * Controlled geoblock field (no react-hook-form). Selection state is owned by
 * the caller; the shared, identical-for-every-consumer data — the countries
 * list and the project's country groups — is fetched internally via React
 * Query. `onModeChange`/`onSelectionChange` mirror the legacy `GeoRestrict`
 * `onGeoOptions`/`onGeoSelection` callbacks so legacy consumers can adopt this
 * base directly later.
 */
export function GeoFieldBase({
  geoblockMode,
  selectedCountries,
  selectedCountryGroups,
  disabled,
  error,
  popoverContainer,
  popoverClassName,
  onModeChange,
  onSelectionChange,
}: GeoFieldBaseProps): JSX.Element {
  const getCountryListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getCountryList,
    params: [],
  });
  const { data: countryData } = useQuery(getCountryListQuery);

  const getCountryGroupListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getCountryGroupList,
    params: [],
  });
  const { data: countryGroupData } = useQuery(getCountryGroupListQuery);

  const hasRestriction = isGeoblockActive(geoblockMode);

  const countryOptionList = useMemo(
    () => getCountryOptions(countryData, false),
    [countryData],
  );

  // A group is stored by reference (a single labelled entry), not expanded
  // into its countries — so we keep a uuid→group map to split the flat
  // combobox value back into groups vs countries on change.
  const groupByUuid = useMemo(
    () =>
      new Map(
        (countryGroupData?.data ?? []).map((countryGroup) => [
          countryGroup.uuid,
          countryGroup,
        ]),
      ),
    [countryGroupData],
  );

  const optionList = useMemo<ComboboxOption[]>(() => {
    const groupOptionList = (countryGroupData?.data ?? [])
      .map((countryGroup) => ({
        value: countryGroup.uuid,
        label: countryGroup.label,
        group: t("editor-components:geoCountryGroupsHeading"),
      }))
      .toSorted((firstGroup, secondGroup) =>
        firstGroup.label.localeCompare(secondGroup.label),
      );
    const taggedCountryOptionList = countryOptionList.map((countryOption) => ({
      ...countryOption,
      group: t("editor-components:geoCountriesHeading"),
    }));
    return [...groupOptionList, ...taggedCountryOptionList];
  }, [countryGroupData, countryOptionList]);

  const selectedValueList = [
    ...selectedCountryGroups.map((countryGroup) => countryGroup.uuid),
    ...selectedCountries.map((country) => country.uuid),
  ];

  const handleSelectionChange = (valueList: string[]): void => {
    const countryGroupList: GeoCountryGroup[] = valueList
      .filter((value) => groupByUuid.has(value))
      .map((uuid) => ({ uuid, label: groupByUuid.get(uuid)?.label }));
    const countryList: GeoCountry[] = valueList
      .filter((value) => !groupByUuid.has(value))
      .map((uuid) => ({
        uuid,
        country: (countryData?.data ?? []).find(
          (country) => country.uuid === uuid,
        )?.country,
      }));
    onSelectionChange({
      countries: countryList,
      countryGroups: countryGroupList,
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <SegmentedFieldBase
        disabled={disabled}
        label={t("editor-components:geographic")}
        value={geoblockMode}
        onValueChange={(mode) => {
          // SegmentedFieldBase is string-typed; its values come only from GEOBLOCK_OPTION_LIST.
          onModeChange(mode as GeoblockMode);
        }}
        options={GEOBLOCK_OPTION_LIST.map((option) => ({
          value: option.value,
          label: t(`editor-components:${option.labelKey}`),
        }))}
      />
      {hasRestriction ? (
        <MultiComboboxBase
          disabled={disabled}
          emptyLabel={t("editor-components:geoNoCountryFound")}
          error={error}
          onChange={handleSelectionChange}
          options={optionList}
          placeholder={t("editor-components:geoCountriesPlaceholder")}
          popoverClassName={popoverClassName}
          popoverContainer={popoverContainer}
          searchPlaceholder={t("editor-components:geoSearchCountry")}
          value={selectedValueList}
        />
      ) : (
        <div className="text-muted-foreground text-[13px]">
          {t("editor-components:geoNoRestrictionInfo")}
        </div>
      )}
    </div>
  );
}

/**
 * Minimal form shape `GeoField` binds to. The backend uses these same field
 * names on every geoblockable entity, so the wrapper stays domain-agnostic
 * (no dependency on offers' `PlanFormValues`).
 */
interface GeoFieldFormValues {
  geoblock_mode?: GeoblockMode;
  countries?: GeoCountry[];
  country_groups?: GeoCountryGroup[];
}

interface GeoFieldProps {
  disabled?: boolean;
  popoverContainer?: HTMLElement | null;
  popoverClassName?: string;
}

/**
 * `GeoFieldBase` bound to react-hook-form via `useFormContext`. Reads/writes
 * `geoblock_mode`, `countries`, `country_groups` on the surrounding form.
 */
export function GeoField({
  disabled,
  popoverContainer,
  popoverClassName,
}: GeoFieldProps): JSX.Element {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<GeoFieldFormValues>();

  const geoblockMode = watch("geoblock_mode") ?? "disabled";
  const selectedCountries = watch("countries") ?? [];
  const selectedCountryGroups = watch("country_groups") ?? [];

  return (
    <GeoFieldBase
      disabled={disabled}
      error={errors.countries?.message}
      geoblockMode={geoblockMode}
      popoverClassName={popoverClassName}
      popoverContainer={popoverContainer}
      selectedCountries={selectedCountries}
      selectedCountryGroups={selectedCountryGroups}
      onModeChange={(mode) =>
        setValue("geoblock_mode", mode, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
      onSelectionChange={({ countries, countryGroups }) => {
        // Set groups without validating, then validate once when setting
        // countries — a single validation pass covers the whole selection.
        setValue("country_groups", countryGroups, { shouldDirty: true });
        setValue("countries", countries, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }}
    />
  );
}
